import { BehaviorSubject, bufferCount, distinctUntilChanged, Observable, skip } from 'rxjs';
import { sum } from 'lodash';
import { Injectable, OnDestroy } from '@angular/core';
import { modifiedSetInterval } from '../utilities/ZoneUtils';
import { FLAGS, FlagsService } from './flags.service';

export enum HeapStatus {
  INSUFFICIENT = 'INSUFFICIENT',
  GOOD = 'GOOD',
}
@Injectable({
  providedIn: 'root',
})
export class HeapMonitoringService implements OnDestroy {
  private readonly HEAP_CHECK_INTERVAL = 10 * 1000; // query the available heap every 10 seconds
  private readonly BYTE_TO_MEGABYTE_CONVERT_FACTOR = 1024 * 1024;
  private readonly totalHeapSize;

  private heapStatusBehaviorSubject = new BehaviorSubject<HeapStatus>(HeapStatus.GOOD);
  private availableHeapSize = new BehaviorSubject<number>(0);
  private heapStatusObservable$ = this.heapStatusBehaviorSubject.asObservable();
  private availableHeapSizeObservable$ = this.availableHeapSize.asObservable();

  private poolingIntervalId;

  constructor(private flagsService: FlagsService) {
    this.totalHeapSize = this.getHeapSizeLimit();

    this.startAvailableHeapSizePooling();

    this.flagsService
      .featureFlagChanged(FLAGS.PERFORMANCE_INTERVENTION_MEMORY)
      .pipe(distinctUntilChanged())
      .subscribe((isFlagEnabled) => {
        if (isFlagEnabled) {
          this.subscribeToAvailableHeapSizeReadings();
        }
      });
  }

  get heapStatus$(): Observable<HeapStatus> {
    return this.heapStatusObservable$;
  }

  get lastHeapStatus(): HeapStatus {
    return this.heapStatusBehaviorSubject.getValue();
  }

  get availableHeapSize$(): Observable<number> {
    return this.availableHeapSizeObservable$;
  }

  get lastAvailableHeapSize(): number {
    return this.availableHeapSize.getValue();
  }

  private getUsedHeapSize() {
    return 'memory' in performance
      ? Math.round((performance as any).memory?.usedJSHeapSize ?? 0) /
          this.BYTE_TO_MEGABYTE_CONVERT_FACTOR // convert to MB
      : 0;
  }

  private getHeapSizeLimit() {
    return 'memory' in performance
      ? Math.round((performance as any).memory?.jsHeapSizeLimit ?? 0) /
          this.BYTE_TO_MEGABYTE_CONVERT_FACTOR // convert to MB
      : 0;
  }

  private startAvailableHeapSizePooling(): void {
    this.poolingIntervalId = modifiedSetInterval(() => {
      const heapSizeRemaining = this.totalHeapSize - this.getUsedHeapSize();
      this.availableHeapSize.next(heapSizeRemaining);
    }, this.HEAP_CHECK_INTERVAL);
  }

  stopAvailableHeapSizePooling(): void {
    if (this.poolingIntervalId) {
      clearInterval(this.poolingIntervalId);
    }
  }

  private subscribeToAvailableHeapSizeReadings(): void {
    const downgradeSamplingWindow =
      (this.flagsService.featureFlagsVariables?.performance_intervention_memory
        ?.downgrade_sample_window as number) ?? 3; // // every 3 checks => 3 * HEAP_CHECK_INTERVAL = 30 seconds

    const upgradeSamplingWindow =
      (this.flagsService.featureFlagsVariables?.performance_intervention_memory
        ?.upgrade_sample_window as number) ?? 6; // every 6 checks => 6 * HEAP_CHECK_INTERVAL = 60 seconds

    this.availableHeapSize
      .pipe(
        // because the first value emitted is not a valid heap read
        skip(1),
        bufferCount(Math.max(downgradeSamplingWindow, upgradeSamplingWindow), 1),
      )
      .subscribe((availableHeapSizes: number[]) => {
        if (availableHeapSizes.length > 0) {
          const upgradeWindow = availableHeapSizes.slice(-upgradeSamplingWindow);
          const upgradedTier = this.getHeapStatusFromReads(upgradeWindow);

          const downgradeWindow = availableHeapSizes.slice(-downgradeSamplingWindow);
          const downgradedTier = this.getHeapStatusFromReads(downgradeWindow);

          if (this.compareHeapStatuses(downgradedTier, this.lastHeapStatus) === -1) {
            this.heapStatusBehaviorSubject.next(downgradedTier);
          } else if (this.compareHeapStatuses(upgradedTier, this.lastHeapStatus) === 1) {
            this.heapStatusBehaviorSubject.next(upgradedTier);
          }
        }
      });
  }

  private compareHeapStatuses(heapStatusA: HeapStatus, heapStatusB: HeapStatus): number {
    const statusToNumbers = {
      [HeapStatus.GOOD]: 2,
      [HeapStatus.INSUFFICIENT]: 1,
    };
    if (statusToNumbers[heapStatusA] > statusToNumbers[heapStatusB]) {
      return 1;
    } else if (statusToNumbers[heapStatusA] < statusToNumbers[heapStatusB]) {
      return -1;
    } else {
      return 0;
    }
  }

  private getHeapStatusFromReads(availableHeapSize: number[]) {
    const minJsHeapSizeLimit =
      (this.flagsService.featureFlagsVariables?.performance_intervention_memory
        ?.minJsHeapSizeLimit as number) ?? 1024;

    const thresholdRemainingHeapSize =
      (this.flagsService.featureFlagsVariables?.performance_intervention_memory
        ?.threshold_remainingHeapSize as number) ?? 1024;

    const heapUsageAverage = sum(availableHeapSize) / availableHeapSize.length;

    return this.totalHeapSize > minJsHeapSizeLimit && heapUsageAverage <= thresholdRemainingHeapSize
      ? HeapStatus.INSUFFICIENT
      : HeapStatus.GOOD;
  }

  ngOnDestroy(): void {
    // cleaning up and releasing resources
    this.availableHeapSize.unsubscribe();
    this.stopAvailableHeapSizePooling();
  }
}
